import React, { useState, useEffect } from 'react';

interface PartnerBooking {
  id: string;
  bookingReference: string;
  createdAt: string;
  status: string;
  subid: string;
  affiliateSource: string;
  customer: {
    name: string;
    email: string;
    phone: string;
    country: string;
  };
  hotel: {
    id: string;
    name: string;
    city: string;
    country: string;
  };
  offer: {
    roomType: string;
    board: string;
    checkIn: string;
    checkOut: string;
    nights: number;
  };
  flights: Array<{
    direction: string;
    airline: string;
    flightNumber: string;
    departureAirport: string;
    arrivalAirport: string;
    departureDate: string;
    arrivalDate: string;
  }>;
  passengersCount: number;
  totalPrice: number;
  currency: string;
  commissionRate: string;
  commissionAmount: number;
}

export const ReisegigantenPartnerPortal: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [token, setToken] = useState<string>('');
  const [username, setUsername] = useState<string>('reisegiganten');
  const [password, setPassword] = useState<string>('');
  const [loginError, setLoginError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const [bookings, setBookings] = useState<PartnerBooking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<PartnerBooking[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [partnerInfo, setPartnerInfo] = useState<any>(null);

  // Restore saved token
  useEffect(() => {
    const savedToken = localStorage.getItem('reisegiganten_partner_token');
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
      fetchBookings(savedToken);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);

    try {
      const res = await fetch('/api/reisegiganten/partner/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setToken(data.token);
        setPartnerInfo(data.partner);
        setIsAuthenticated(true);
        localStorage.setItem('reisegiganten_partner_token', data.token);
        fetchBookings(data.token);
      } else {
        setLoginError(data.error || 'Ugyldig brukernavn eller passord');
      }
    } catch (err: any) {
      setLoginError('Kunne ikke koble til serveren');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('reisegiganten_partner_token');
    setToken('');
    setIsAuthenticated(false);
    setBookings([]);
  };

  const fetchBookings = async (authToken: string) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/reisegiganten/partner/bookings?token=${authToken}`);
      const data = await res.json();
      if (data.success && Array.isArray(data.bookings)) {
        setBookings(data.bookings);
        setFilteredBookings(data.bookings);
      }
    } catch (err) {
      console.error('Failed to fetch partner bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter effect
  useEffect(() => {
    let result = [...bookings];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(b => 
        (b.bookingReference && b.bookingReference.toLowerCase().includes(term)) ||
        (b.customer?.name && b.customer.name.toLowerCase().includes(term)) ||
        (b.customer?.email && b.customer.email.toLowerCase().includes(term)) ||
        (b.hotel?.name && b.hotel.name.toLowerCase().includes(term)) ||
        (b.subid && b.subid.toLowerCase().includes(term))
      );
    }
    if (statusFilter !== 'all') {
      result = result.filter(b => (b.status || '').toLowerCase() === statusFilter.toLowerCase());
    }
    setFilteredBookings(result);
  }, [searchTerm, statusFilter, bookings]);

  // Calculations
  const totalRevenue = filteredBookings.reduce((sum, b) => sum + (b.totalPrice || 0), 0);
  const totalCommission = filteredBookings.reduce((sum, b) => sum + (b.commissionAmount || 0), 0);
  const confirmedCount = filteredBookings.filter(b => (b.status || '').toLowerCase() === 'confirmed').length;

  const exportToCSV = () => {
    if (filteredBookings.length === 0) return;
    const headers = ['Booking Ref', 'Date', 'Status', 'SubID', 'Customer Name', 'Customer Email', 'Hotel Name', 'Check-In', 'Check-Out', 'Nights', 'Total Price', 'Currency', 'Commission (5%)'];
    const rows = filteredBookings.map(b => [
      b.bookingReference,
      new Date(b.createdAt).toLocaleDateString(),
      b.status,
      b.subid || 'N/A',
      `"${b.customer?.name || ''}"`,
      b.customer?.email || '',
      `"${b.hotel?.name || ''}"`,
      b.offer?.checkIn || '',
      b.offer?.checkOut || '',
      b.offer?.nights || 0,
      b.totalPrice || 0,
      b.currency || 'SEK',
      b.commissionAmount || 0
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Reisegiganten_Bookings_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-800 rounded-2xl border border-slate-700 shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-600/20 text-blue-400 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-blue-500/30">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Reisegiganten Partner Portal</h1>
            <p className="text-slate-400 text-sm mt-1">Sikker tilgang for Lumitours booking-sporing</p>
          </div>

          {loginError && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm text-center">
              {loginError}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Brukernavn / Partner ID</label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                required
                className="w-full px-4 py-3 bg-slate-900/80 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-blue-500 transition-colors"
                placeholder="reisegiganten"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Passord</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 bg-slate-900/80 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-blue-500 transition-colors"
                placeholder="••••••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 px-4 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl shadow-lg shadow-blue-600/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Logger inn...</span>
                </>
              ) : (
                <span>Logg inn på Partner Portal</span>
              )}
            </button>
          </form>

          <div className="mt-8 text-center text-xs text-slate-500 border-t border-slate-700/50 pt-4">
            Lumitours Platform &copy; 2026 • Affiliate Management System
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 bg-slate-900 p-6 rounded-2xl border border-slate-800 shadow-xl">
          <div>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-blue-500/10 text-blue-400 border border-blue-500/20 text-xs font-bold rounded-full uppercase tracking-wider">Verified Partner</span>
              <span className="text-slate-400 text-sm">Market: Sweden, Denmark, Norway, Finland</span>
            </div>
            <h1 className="text-2xl font-bold text-white mt-1">Reisegiganten Booking Tracking Portal</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchBookings(token)}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Oppdater data</span>
            </button>
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-sm font-medium shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Eksporter CSV</span>
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-rose-600/10 hover:bg-rose-600/20 border border-rose-500/30 text-rose-400 rounded-xl text-sm font-medium transition-colors"
            >
              Logg ut
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-slate-900 p-5 rounded-2xl border border-slate-800 shadow-lg">
            <div className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Total Bookinger</div>
            <div className="text-3xl font-extrabold text-white mt-2">{filteredBookings.length}</div>
            <div className="text-emerald-400 text-xs mt-1">Bekreftet: {confirmedCount}</div>
          </div>
          <div className="bg-slate-900 p-5 rounded-2xl border border-slate-800 shadow-lg">
            <div className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Brutto Omsetning</div>
            <div className="text-3xl font-extrabold text-blue-400 mt-2">{Math.round(totalRevenue).toLocaleString('sv-SE')} SEK</div>
            <div className="text-slate-400 text-xs mt-1">Totalt fra sporet salg</div>
          </div>
          <div className="bg-slate-900 p-5 rounded-2xl border border-slate-800 shadow-lg border-l-4 border-l-emerald-500">
            <div className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Estimert Provisjon (6%)</div>
            <div className="text-3xl font-extrabold text-emerald-400 mt-2">{Math.round(totalCommission).toLocaleString('sv-SE')} SEK</div>
            <div className="text-emerald-400 text-xs mt-1">Inntjening Reisegiganten</div>
          </div>
          <div className="bg-slate-900 p-5 rounded-2xl border border-slate-800 shadow-lg">
            <div className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Aktive Kampanjer / SubID</div>
            <div className="text-3xl font-extrabold text-amber-400 mt-2">
              {new Set(filteredBookings.map(b => b.subid).filter(Boolean)).size}
            </div>
            <div className="text-slate-400 text-xs mt-1">Unike sporingskoder</div>
          </div>
        </div>

        {/* Filter Toolbar */}
        <div className="bg-slate-900 p-4 rounded-2xl border border-slate-800 flex flex-wrap items-center justify-between gap-4">
          <div className="flex-1 min-w-[280px]">
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Søk på booking ref, kundenavn, e-post, hotell eller SubID..."
              className="w-full px-4 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs font-semibold text-slate-400 uppercase">Status:</label>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:border-blue-500"
            >
              <option value="all">Alle Statuser</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Pending">Pending</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Bookings Data Table */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-300">
              <thead className="bg-slate-950 text-slate-400 text-xs uppercase tracking-wider font-semibold border-b border-slate-800">
                <tr>
                  <th className="px-5 py-4">Booking Ref / Dato</th>
                  <th className="px-5 py-4">Kunde</th>
                  <th className="px-5 py-4">Hotell & Destinasjon</th>
                  <th className="px-5 py-4">Rom & Måltid</th>
                  <th className="px-5 py-4">Innsjekk / Utsjekk</th>
                  <th className="px-5 py-4">Flydetaljer</th>
                  <th className="px-5 py-4">Pris & Provisjon</th>
                  <th className="px-5 py-4">SubID</th>
                  <th className="px-5 py-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-5 py-12 text-center text-slate-500">
                      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                      Laster inn bookinger...
                    </td>
                  </tr>
                ) : filteredBookings.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-5 py-12 text-center text-slate-500">
                      Ingen bookinger funnet for gjeldende søkekriterier.
                    </td>
                  </tr>
                ) : (
                  filteredBookings.map((b) => (
                    <tr key={b.id} className="hover:bg-slate-800/50 transition-colors">
                      <td className="px-5 py-4">
                        <div className="font-bold text-white">{b.bookingReference}</div>
                        <div className="text-xs text-slate-400">{new Date(b.createdAt).toLocaleString('sv-SE')}</div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-medium text-slate-200">{b.customer?.name}</div>
                        <div className="text-xs text-slate-400">{b.customer?.email}</div>
                        <div className="text-xs text-slate-500">{b.customer?.phone}</div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-medium text-slate-200">{b.hotel?.name}</div>
                        <div className="text-xs text-slate-400">{b.hotel?.city || b.hotel?.country}</div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-xs font-semibold text-blue-400">{b.offer?.roomType}</div>
                        <div className="text-xs text-slate-400">{b.offer?.board}</div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-xs text-emerald-400 font-mono">In: {b.offer?.checkIn}</div>
                        <div className="text-xs text-rose-400 font-mono">Ut: {b.offer?.checkOut}</div>
                        <div className="text-xs text-slate-500">{b.offer?.nights} netter • {b.passengersCount} pers</div>
                      </td>
                      <td className="px-5 py-4">
                        {b.flights && b.flights.length > 0 ? (
                          b.flights.map((f, idx) => (
                            <div key={idx} className="text-xs font-mono text-slate-300">
                              <span className="text-blue-400 font-bold">{f.flightNumber}</span> ({f.departureAirport} → {f.arrivalAirport})
                            </div>
                          ))
                        ) : (
                          <span className="text-xs text-slate-500 italic">Kun hotell / TBD</span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-bold text-white">{Math.round(b.totalPrice).toLocaleString('sv-SE')} {b.currency}</div>
                        <div className="text-xs text-emerald-400 font-semibold">Provisjon: +{Math.round(b.commissionAmount).toLocaleString('sv-SE')} {b.currency}</div>
                      </td>
                      <td className="px-5 py-4">
                        <span className="px-2.5 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 text-xs font-mono rounded-lg">
                          {b.subid || 'N/A'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                          (b.status || '').toLowerCase() === 'confirmed'
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                            : 'bg-amber-500/10 text-amber-400 border border-amber-500/30'
                        }`}>
                          {b.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};
